import React from 'react';
import ReactDOM from 'react-dom';
import { useEditor, useNode } from '@craftjs/core';
import { useSelector } from 'react-redux';
import styles from './styles/styles.module.css';
import { useInteractionMode } from '../contexts/InteractionMode';

/** Portal so the modal sits above all stacking contexts */
function BodyPortal({ children }: { children: React.ReactNode }) {
  const el = React.useMemo(() => document.createElement('div'), []);
  React.useEffect(() => {
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, [el]);
  return ReactDOM.createPortal(children, el);
}

const DropdownField = ({
  label,
  options = [],
  value,
  left = 0,
  top = 0,
  width = 200,
  height = 40,
  required = false, // builder-time default
}: any) => {
  const { enabled, actions } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const {
    connectors: { connect },
    setProp,
    id,
  } = useNode();

  const interactionMode = useInteractionMode();
  const { selected } = useSelector((s: any) => s.formToggle);

  const [isDragging, setDragging] = React.useState(false);
  const [isResizing, setResizing] = React.useState(false);
  const [showEditor, setShowEditor] = React.useState(false);

  const [optText, setOptText] = React.useState('');

  const onMouseDown = (e: React.MouseEvent) => {
    if (!enabled || showEditor || isResizing) return;
    e.preventDefault();
    setDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialLeft = left;
    const initialTop = top;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.left = initialLeft + dx;
        props.top = initialTop + dy;
      });
    };

    const onMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onResizeMouseDown = (e: React.MouseEvent) => {
    if (!enabled) return;
    e.stopPropagation();
    e.preventDefault();
    setResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialWidth = width;
    const initialHeight = height;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.width = Math.max(100, initialWidth + dx);
        props.height = Math.max(30, initialHeight + dy);
      });
    };

    const onMouseUp = () => {
      setResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onDoubleClick = () => {
    if (!enabled) return;
    if (selected === 'FORM') {
      const current = Array.isArray(options) ? options : [];
      setOptText(current.join(', '));
      setShowEditor(true);
    }
  };

  React.useEffect(() => {
    if (!showEditor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEditor(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEditor]);

  const handleDelete = () => {
    if (selected === 'FORM') actions.delete(id);
  };

  const commitOptions = React.useCallback(() => {
    const list = optText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    setProp((props: any) => {
      props.options = list;
      if (!list.includes(props.value)) {
        props.value = list[0] ?? '';
      }
    });
    setShowEditor(false);
  }, [optText, setProp]);

  function cls() {
    if (interactionMode === 'VIEW') {
      return {
        wrapper: styles.wrapperReport,
        label: styles.labelReport,
        inputContainer: styles.inputContainerReport,
        select: styles.selectReport,
      };
    }
    return {
      wrapper: styles.wrapperForm,
      label: styles.labelForm,
      inputContainer: styles.inputContainerForm,
      select: styles.selectForm,
    };
  }

  const classes = cls();
  const opts: string[] = Array.isArray(options) ? options : [];
  const selectedValue = (value ?? opts[0] ?? '').toString();
  const isFill = interactionMode === 'FILL';
  const isForm = selected === 'FORM';
  const isView = interactionMode === 'VIEW';

  return (
    <>
      <div
        ref={(ref) => ref && connect(ref)}
        className={classes.wrapper}
        style={{
          left,
          top,
          position: 'absolute',
          cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
      >
        {interactionMode !== 'VIEW' && (
          <label className={classes.label}>
            {label}
            {required && (
              <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                *
              </span>
            )}
            {enabled && (
              <div className={styles.deleteButton} onClick={handleDelete}>
                ×
              </div>
            )}
          </label>
        )}

        {isView && (
          <div className={styles.viewContainer}>
            <p className={styles.viewLabel}>
              {label}
              {required && (
                <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                  *
                </span>
              )}
            </p>
            <p className={styles.viewValue}>{value}</p>
          </div>
        )}

        {interactionMode !== 'VIEW' && (
          <div className={classes.inputContainer} style={{ width, height }}>
            <select
              className={classes.select}
              value={selectedValue}
              required={isFill ? !!required : false}
              aria-required={isFill ? !!required : false}
              disabled={enabled}
              onChange={(e) =>
                isFill &&
                setProp((p: any) => {
                  p.value = e.target.value;
                })
              }
              style={{ pointerEvents: enabled ? 'none' : 'auto' }}
            >
              {opts.map((opt: string, i: number) => (
                <option key={i} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {enabled && isForm && (
              <div
                onMouseDown={onResizeMouseDown}
                className={styles.resizeHandle}
              />
            )}
          </div>
        )}
      </div>

      {showEditor && isForm && (
        <BodyPortal>
          <div
            className={styles.modalOverlay}
            role="presentation"
            onMouseDown={() => setShowEditor(false)}
          >
            <div
              className={styles.modalPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Edit dropdown"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Edit Dropdown</h3>
                <button
                  className={styles.modalClose}
                  aria-label="Close"
                  onClick={() => setShowEditor(false)}
                >
                  ×
                </button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formRow}>
                  <label className={styles.rowLabel}>Label</label>
                  <input
                    className={styles.rowInput}
                    type="text"
                    value={label}
                    onChange={(e) =>
                      setProp((props: any) => (props.label = e.target.value))
                    }
                  />
                </div>

                <div className={styles.formRow}>
                  <label className={styles.rowLabel}>
                    Options (comma-separated)
                  </label>
                  <input
                    className={styles.rowInput}
                    type="text"
                    value={optText}
                    onChange={(e) => setOptText(e.target.value)}
                    placeholder="e.g. Small, Medium, Large"
                  />
                </div>

                {/* New: builder-time required field toggle */}
                <div
                  className={styles.formRowInline}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    id={`required-${id}`}
                    type="checkbox"
                    checked={!!required}
                    onChange={(e) =>
                      setProp((props: any) => {
                        props.required = e.target.checked;
                      })
                    }
                  />
                  <label
                    style={{ marginLeft: '8px' }}
                    htmlFor={`required-${id}`}
                  >
                    Required field
                  </label>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button className={styles.btnPrimary} onClick={commitOptions}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
};

(DropdownField as any).craft = {
  displayName: 'DropdownField',
  props: {
    label: 'Choose an option',
    options: ['Option 1', 'Option 2', 'Option 3'],
    value: '',
    left: 0,
    top: 0,
    width: 200,
    height: 40,
    required: false, // default off
  },
};

export default DropdownField;
